import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { reduxForm } from 'redux-form';
import { compose } from 'redux';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import mixpanel from 'mixpanel-browser';
import { store } from 'store';
import { Button, Input, ActionList } from 'common/components';
import { graphql } from 'react-apollo';
import { MUTATION_UPDATE_CROPS_FOR_FIELDS } from 'api/crop';
import { numberOnly, minValueField } from 'common/form-validators';
import { trim } from 'common/normalizers';
import { graphQlToFormErrors } from 'common/utils';
import { show, hide } from '../../actions/banner';
import { isUpdatingCropPrice } from '../../actions/settings';
import * as constants from 'common/constants';
import { valuesPolyfill } from 'common/polyFills';

const ObjectValues = Object.values || valuesPolyfill;

/**
 * Form Column Header: Update accordingly when renderFields() column changes
 */
const columnHeader = [
  {
    content: (
      <Fragment>
        <div className="column">Crops</div>
        <div className="column">Price (Per Bushel)</div>
      </Fragment>
    ),
  },
];

/**
 *
 *
 * @function processVariety
 * Does a check to see if variety is defined and if falsey return an empty string
 */
const processVariety = variety => variety || '';

export class RevenueForm extends PureComponent {
  positiveNumOnly = value => value && minValueField(value, 0);

  duplicates = [];

  _handleSubmit = values => {
    let fieldCropsUpdateInput = [];
    for (const id in values) {
      if (values[id]) {
        const cropField = {
          id,
          expectedPrice: { value: parseFloat(values[id]) },
        };

        // Find currently handled duplicate input
        const dupes = ObjectValues(this.duplicates).filter(dupeCrop => {
          // Destruct from ['CROPFIELD', season, field, crop, variety ]
          // Here we only want crop name and crop variety
          const cropVariety = this.getCropVariatyFromId(dupeCrop.id);
          return cropField.id.includes(cropVariety);
        });

        fieldCropsUpdateInput.push(cropField);

        // Update the hidden input values with the value of the used input
        dupes.forEach(dupe => {
          const cropVariety = this.getCropVariatyFromId(dupe.id);

          const currentDupIndex = this.getCurrentCropIndex(
            fieldCropsUpdateInput,
            cropVariety
          );
          if (currentDupIndex > -1) {
            cropField.expectedPrice =
              fieldCropsUpdateInput[currentDupIndex].expectedPrice;
          }
        });
      }
    }

    this.props.onUpdateCrops(fieldCropsUpdateInput);
    this.props.show(
      constants.BANNER_IDS.APP,
      constants.DASHBOARD_MESSAGES.CROP_PRICES_UPDATED
    );

    setTimeout(() => {
      this.props.hide(constants.BANNER_IDS.APP);
    }, 3000);

    this.props.onSubmit();
  };

  componentDidUpdate(prevProps) {
    /**
     *
     * Close the form if the user switches seasons
     */
    if (prevProps.season !== this.props.season) {
      this.props.onCancel();
    }
  }

  render() {
    const {
      handleSubmit,
      valid,
      pristine,
      submitting,
      crops,
      error,
    } = this.props;
    this.duplicates = this.processDuplicateCrops(crops);

    return (
      <form onSubmit={handleSubmit(this._handleSubmit)}>
        {crops && crops.length > 0 && (
          <Fragment>
            <p>
              Please update expected prices for the surplus/uncontracted grains.
            </p>
            <ActionList
              theme={['columns', 'columns__header']}
              cards={columnHeader}
            />
            <ActionList
              theme={['columns']}
              cards={crops.map(
                ({ crop: { name, variety }, season, fieldName }, index) => {
                  const cropVariety = `${name} ${processVariety(variety)}`;
                  let inputIsHidden = '';

                  if (
                    this.duplicates[cropVariety] &&
                    this.duplicates[cropVariety].indices.includes(index)
                  ) {
                    inputIsHidden = 'is-hidden';
                  }

                  return {
                    content: (
                      <Fragment>
                        <div className={`column ${inputIsHidden}`}>
                          <h6 className={'text--tight'}>{name}</h6>
                          {variety && <small>{variety}</small>}
                        </div>
                        <div className={`column ${inputIsHidden}`}>
                          <Input
                            name={`CROPFIELD:${season}:${fieldName}:${name}:${processVariety(
                              variety
                            )}`}
                            preText="$"
                            validate={[numberOnly, this.positiveNumOnly]}
                            validateOnChange
                            normalizeOnBlur={trim}
                          />
                        </div>
                      </Fragment>
                    ),
                  };
                }
              )}
            />

            {error && <small className={'text--error'}>{error}</small>}

            <div className={'field__group'}>
              <fieldset className={'field text--center'}>
                <Button
                  type="submit"
                  className="btn btn--primary gap-top-small"
                  disabled={!valid || pristine || submitting}
                >
                  Update
                </Button>
              </fieldset>

              <fieldset className={'field text--center'}>
                <Button
                  type="cancel"
                  className="btn"
                  onClick={this.onCancel}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </fieldset>
            </div>
          </Fragment>
        )}
      </form>
    );
  }

  /**
   *
   *
   * @memberof RevenueForm
   */
  processDuplicateCrops = () => {
    const { crops } = this.props;
    if (!crops || !crops.length) return [];

    this.duplicates = crops.reduce((acc, cur, indexNumber, array) => {
      const cropVariety = `${cur.crop.name} ${processVariety(
        cur.crop.variety
      )}`;
      const id = `CROPFIELD:${cur.season}:${cur.fieldName}:${
        cur.crop.name
      }:${processVariety(cur.crop.variety)}`;

      // If the current CropVariety appears more than once,
      // record it's location/index in the crop array so we can
      // identifiy them later in the form for hiding etc.
      if (this.getNumberOfCurrCrop(array, cropVariety) > 1) {
        acc[cropVariety] = {
          occurences: Number((acc[cropVariety] || {}).occurences || 0) + 1,
          indices: [...((acc[cropVariety] || {}).indices || []), indexNumber],
          id,
        };
      }
      return acc;
    }, {});

    // Find first occurence of the duplicate and remove it
    // (this will be the one we show) from our list of duplicates
    ObjectValues(this.duplicates).forEach(obj => {
      const lowest = Math.min(...obj.indices);
      const indexOfLowest = obj.indices.indexOf(lowest);
      obj.indices.splice(indexOfLowest, 1);
      // obj.indexOfVisibleInput = lowest;
    });

    return this.duplicates;
  };

  /**
   *
   *
   * @memberof RevenueForm
   */
  getNumberOfCurrCrop = (array, cropVariety) => {
    return array.filter(item => {
      const cv = `${item.crop.name} ${processVariety(item.crop.variety)}`;
      return cv === cropVariety;
    }).length;
  };

  /**
   *
   *
   * @memberof RevenueForm
   */
  getCurrentCropIndex = (array, cropVariety) => {
    return array.findIndex(item => {
      const cropNameVariety = this.getCropVariatyFromId(item.id);
      return cropVariety === cropNameVariety;
    });
  };

  /**
   *
   *
   * @memberof RevenueForm
   */
  getCropVariatyFromId = id => {
    const [, , , cropName, variety] = id.split(':');
    const cropVariety = [cropName, variety].join(':');
    return cropVariety;
  };

  /**
   *
   *
   * @memberof RevenueForm
   */
  onCancel = event => {
    event.preventDefault();
    this.props.onCancel();
  };
}

RevenueForm.propTypes = {
  // from redux form
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  initialValue: PropTypes.array,

  // manually added
  onCancel: PropTypes.func,
  crops: PropTypes.arrayOf(PropTypes.object),
  season: PropTypes.number.isRequired,
  onUpdateCrops: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  show: PropTypes.func,
  hide: PropTypes.func,
};

const formSettings = {
  form: 'revenueform',
  enableReinitialize: true,
};

const buildInitialValues = (crops, seasonFromState) => {
  const initialValues = {};

  (crops || []).forEach(({ crop, expectedPrice, fieldName }) => {
    initialValues[
      `CROPFIELD:${seasonFromState}:${fieldName}:${crop.name}:${processVariety(
        crop.variety
      )}`
    ] = expectedPrice && expectedPrice.value;
  });

  return initialValues;
};

const mapState = ({ settings }, ownProps) => {
  const { crops } = ownProps;
  const seasonFromState = settings.season;

  let initialValues = buildInitialValues(crops, seasonFromState);

  return {
    initialValues,
    season: seasonFromState,
    ...ownProps,
  };
};

const mapProps = dispatch => bindActionCreators({ show, hide }, dispatch);

export default compose(
  graphql(MUTATION_UPDATE_CROPS_FOR_FIELDS, {
    props: props => ({
      onUpdateCrops: fieldCrops => {
        store.dispatch(isUpdatingCropPrice(true));

        return props
          .mutate({
            variables: { fieldCrops },
          })
          .then(res => {
            if (res.data.updateCropsForFields) {
              mixpanel.track(constants.ANALYTICS.EVENTS.CROP.PRICE_UPDATED);
            }
            store.dispatch(isUpdatingCropPrice(false));
            return res;
          })
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),
  connect(
    mapState,
    mapProps
  ),
  reduxForm(formSettings)
)(RevenueForm);



// WEBPACK FOOTER //
// ./src/containers/RevenueForm/index.js